// The branded confirm for a Client Portal login or invite on a customer with NO ACTIVE contract (Session PU1).
//
// The server refuses 409 portal_no_active_contract for that customer (server/src/modules/portal/portalInviteGuard.ts)
// unless the request carries confirmNoActiveContract. The office pages catch that refusal, show THIS confirm naming the
// customer and the recipient, and only on the person's yes send the same request again with the confirmation. Nothing
// is stored: the next request asks the server again, so a contract signed meanwhile simply removes the question.
//
// WORDING. "invite" is a REAL email with a sign-in code (never a draft), so it says so. "create" makes the login only
// and sends nothing, so it says that instead.
import { ApiError } from "../lib/api";
import { Button } from "./Button";
import { ModalShell } from "./ModalShell";

export const PORTAL_NO_ACTIVE_CONTRACT = "portal_no_active_contract";

// True when this error is the server's no-active-contract refusal (read from the parsed body, because ApiError.code
// carries the server's message wording when there is one).
export function isNoActiveContractRefusal(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.body?.error === PORTAL_NO_ACTIVE_CONTRACT;
}

export function NoActiveContractConfirm({
  kind,
  customerName,
  recipient,
  busy,
  onConfirm,
  onCancel,
}: {
  kind: "create" | "invite";
  customerName: string;
  recipient: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const invite = kind === "invite";
  return (
    <ModalShell ariaLabel="This customer has no active contract" onClose={() => !busy && onCancel()}>
      <h2 className="mb-2 font-heading text-xl text-ele-strong">This customer has no active contract</h2>
      <p className="mb-3 text-sm text-ele-body">
        {customerName} has no active contract at the moment, so a Client Portal login for them is premature.
      </p>
      <p className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-inset px-3 py-2 text-sm text-ele-strong">
        {invite ? (
          <>
            A real email with a sign-in code will go to <strong className="break-all">{recipient}</strong> straight away. It is not a draft,
            and nobody reviews it first.
          </>
        ) : (
          <>
            This will create a Client Portal login for <strong className="break-all">{recipient}</strong>. No email is sent until you
            press Send invite.
          </>
        )}
      </p>
      <div className="flex flex-wrap justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {invite ? "Do not send" : "Do not create"}
        </Button>
        <Button onClick={onConfirm} loading={busy}>
          {invite ? "Yes, send it to this address" : "Yes, create the login"}
        </Button>
      </div>
    </ModalShell>
  );
}
